import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DSSetup, Service } from 'webapp-types';
import { MetricFilterOperator } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import BlockSettingsFiltersComponent from 'webapp/components/block/settings/filters';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Options } from 'webapp/components/form/select-attributes';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';

import type { MetricSetup } from 'webapp/models-custom';
import type Block from 'webapp/models-custom/block';
import type DataSelect from 'webapp/models-custom/data-select';
import type DateRange from 'webapp/models-custom/date-range';

interface Args {
	metricSetup: MetricSetup;
	blockM: Block | null;
	dateRange?: DateRange | null;
	isDisabled?: boolean;
	trackingOrigin?: string;
}

export default class MetricDetailsSettingsFilterSectionComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;

	get firstDataSelect(): DataSelect | null {
		return this.args.blockM?.blockSetup?.firstShownDataSelect ?? null;
	}

	get showFilters(): boolean {
		return this.showAdvancedFilters || !!this.args.metricSetup?.allowBasicDimensionValueFilters;
	}

	get showAdvancedFilters(): boolean {
		return (
			!!this.args.metricSetup?.allowDimensions && !!this.firstDataSelect?.allowAdvancedFilters
		);
	}

	get selectedAttributes(): string[] {
		return this.args.metricSetup?.attributeNamesFromBasicFilters ?? [];
	}

	get loadValueOptions() {
		return this.args.metricSetup?.metric?.isTypeHorizon
			? this.loadHorizonValueOptions
			: undefined;
	}

	changeAttributes = (attributes: string[]) => {
		this.args.metricSetup.setFiltersFromAttributeNames(attributes);
		if (this.args.trackingOrigin) {
			this.analyticsService.event(TrackingEvent.FILTER_DATA_SET, {
				origin: this.args.trackingOrigin,
				filters: attributes.length,
			});
		}
	};

	loadHorizonValueOptions = (): Promise<Options> => {
		return this.mmHorizonMetricsService.getValueOptionsForField(
			this.args.metricSetup,
			this.args.metricSetup.dimensions[0] || '',
			MetricFilterOperator.ANY_OF,
			this.args.dateRange ?? undefined,
		);
	};

	changeFilters = (filters: DSSetup.MetricSetupFiltersData | null) => {
		this.args.metricSetup.setFiltersFromData(filters);
	};

	<template>
		{{#if this.showFilters}}
			<div class="metric-explorer-settings__section metric-explorer-settings__filter-section">
				<div class="metric-explorer-settings__section-header">
					<CommonUiIconComponent @name="Filter" @size="sm" />
					Filters
				</div>
				{{#if this.showAdvancedFilters}}
					{{#if @blockM}}
						{{#if this.firstDataSelect}}
							<BlockSettingsFiltersComponent
								@blockM={{@blockM}}
								@model={{this.firstDataSelect}}
								@isDisabled={{@isDisabled}}
								@origin={{@trackingOrigin}}
								@onFiltersChange={{this.changeFilters}}
							/>
						{{/if}}
					{{/if}}
				{{else}}
					<FormSelectAttributesComponent
						@selected={{this.selectedAttributes}}
						@metricSetupForQuery={{@metricSetup}}
						@dateRangeForQuery={{@dateRange}}
						@onChange={{this.changeAttributes}}
						@isMultiple={{true}}
						@showFloatlabel={{false}}
						@placeholder="Select"
						@isDisabled={{@isDisabled}}
						@loadOptions={{this.loadValueOptions}}
					/>
				{{/if}}
			</div>
		{{/if}}
	</template>
}
